import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Database, 
  Search, 
  Brain, 
  Users, 
  Calendar,
  ArrowRight,
  Shield,
  Zap
} from 'lucide-react';

const Sep4StatusReport = () => {
  const demos = [
    {
      path: '/sep4/vector-db',
      name: 'Vector Database',
      icon: <Database className="w-8 h-8 text-orange-600" />,
      description: 'Policy Content Intent Matching',
      details: 'Explore how AI understands the intent behind different policy provisions'
    },
    {
      path: '/sep4/rag',
      name: 'RAG Dashboards',
      icon: <Search className="w-8 h-8 text-blue-600" />,
      description: 'Retrieval-Augmented Generation for Insurance',
      details: 'See how AI serves different information to broker and client portals'
    },
    {
      path: '/sep4/ai-agents',
      name: 'AI Agents',
      icon: <Brain className="w-8 h-8 text-green-600" />,
      description: 'Autonomous Insurance Workflows',
      details: 'Watch autonomous agents automate insurance workflows 24/7'
    },
    {
      path: '/sep4/moe',
      name: 'Mixture of Experts',
      icon: <Users className="w-8 h-8 text-purple-600" />,
      description: 'Specialized Insurance Analysis',
      details: 'See how AI activates specialized insurance experts for different claim types'
    }
  ];

  return (
    <div className="home-page">
      <div className="home-content">
        <h1 className="home-title">
          <Calendar className="w-12 h-12 text-blue-600 mb-4" />
          September 4th Status Report
        </h1>
        <p className="home-subtitle">
          AI Technology Demonstrations for Construction Insurance
        </p>
        
        <div className="demo-grid">
          {demos.map((demo) => (
            <div key={demo.path} className="demo-card">
              <div className="flex justify-center mb-3">
                {demo.icon}
              </div>
              <h3>{demo.name}</h3>
              <p>{demo.details}</p>
              <div className="mt-4 mb-4">
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {demo.description}
                </span>
              </div>
              <Link to={demo.path} className="demo-link">
                View Demo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        {/* Key Benefits Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-100 border border-blue-200 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
            <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            AI Technology Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">⚡</div>
              <h3 className="font-semibold text-gray-800 mb-2">Faster Processing</h3>
              <p className="text-sm text-gray-600">Reduction in processing time</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🎯</div>
              <h3 className="font-semibold text-gray-800 mb-2">Better Accuracy</h3>
              <p className="text-sm text-gray-600">Specialized experts for each document type</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">💰</div>
              <h3 className="font-semibold text-gray-800 mb-2">Cost Savings</h3>
              <p className="text-sm text-gray-600">Reduction in processing costs</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔒</div>
              <h3 className="font-semibold text-gray-800 mb-2">Smart Access</h3>
              <p className="text-sm text-gray-600">Role-based information delivery</p>
            </div>
          </div>
        </div>

        {/* Technology Overview */}
        <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-100 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-green-800 mb-4">
            <Zap className="w-6 h-6 text-green-600 inline mr-2" />
            Technology Stack Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-green-700 mb-3">Core Technologies</h4>
              <ul className="text-sm text-green-600 space-y-1">
                <li>• Vector databases for semantic understanding</li>
                <li>• RAG systems for contextual information retrieval</li>
                <li>• AI agents for autonomous workflow automation</li>
                <li>• Mixture of Experts for specialized analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-3">Business Applications</h4>
              <ul className="text-sm text-green-600 space-y-1">
                <li>• Construction insurance claim processing</li>
                <li>• Risk assessment and underwriting</li>
                <li>• Policy content analysis and matching</li>
                <li>• Broker and client portal optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sep4StatusReport;
